import { useEffect } from "react";
import {
  CONSENT_STORAGE_KEY,
  CONSENT_CHANGE_EVENT,
  getStoredAnalyticsConsent,
  getStoredConsentValue,
  initAnalytics,
  trackCtaClick,
  trackOutboundClick,
  trackPricingPlanSelect,
  trackScrollDepth,
  trackSectionView,
  setConsent,
} from "./ga4";
import { scheduleWhenIdle } from "@/lib/browser-idle";

const SCROLL_THRESHOLDS = [25, 50, 75, 90];
const SESSION_SECTION_PREFIX = "rugira_section_view";
const SESSION_SCROLL_PREFIX = "rugira_scroll_depth";

const getSectionId = (element?: Element | null) => {
  if (!element || !(element instanceof HTMLElement)) return undefined;
  return (
    element.dataset.sectionId ||
    element.dataset.ctaSection ||
    element.closest<HTMLElement>("[data-section-id]")?.dataset.sectionId
  );
};

const getLabel = (element?: Element | null, fallback?: string) => {
  if (!element || !(element instanceof HTMLElement)) return fallback;
  return (
    element.dataset.ctaLabel ||
    element.dataset.linkLabel ||
    element.getAttribute("aria-label") ||
    element.textContent ||
    fallback
  );
};

const getHref = (element?: Element | null) => {
  if (!element || !(element instanceof HTMLElement)) return undefined;
  return (
    element.dataset.href ||
    element.getAttribute("href") ||
    element.closest<HTMLAnchorElement>("a[href]")?.getAttribute("href") ||
    undefined
  );
};

const isSessionMarked = (key: string) => {
  try {
    return sessionStorage.getItem(key) === "true";
  } catch {
    return false;
  }
};

const markSession = (key: string) => {
  try {
    sessionStorage.setItem(key, "true");
  } catch {
    // Ignore storage errors.
  }
};

export const useAnalyticsConsent = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    initAnalytics();

    const applyConsent = (analytics: boolean) => {
      void setConsent({ analytics });
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key !== CONSENT_STORAGE_KEY) return;
      applyConsent(event.newValue === "true");
    };

    const onConsentChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ analytics: boolean }>;
      applyConsent(Boolean(customEvent.detail?.analytics));
    };

    const cancelIdle = scheduleWhenIdle(() => {
      const storedConsent = getStoredConsentValue();
      if (storedConsent === null) return;
      applyConsent(getStoredAnalyticsConsent());
    }, 1500);

    window.addEventListener("storage", onStorage);
    window.addEventListener(CONSENT_CHANGE_EVENT, onConsentChange as EventListener);
    return () => {
      cancelIdle();
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(CONSENT_CHANGE_EVENT, onConsentChange as EventListener);
    };
  }, []);
};

export const useSectionObserver = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    let observer: IntersectionObserver | undefined;

    const cancelIdle = scheduleWhenIdle(() => {
      const sections = Array.from(document.querySelectorAll<HTMLElement>("[data-section-id]"));
      if (!sections.length) return;

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
            const sectionId = (entry.target as HTMLElement).dataset.sectionId;
            if (!sectionId) return;
            const key = `${SESSION_SECTION_PREFIX}:${window.location.pathname}:${sectionId}`;
            if (isSessionMarked(key)) return;
            markSession(key);
            trackSectionView(sectionId);
          });
        },
        { threshold: [0.5] },
      );

      sections.forEach((section) => observer?.observe(section));
    }, 2000);

    return () => {
      cancelIdle();
      observer?.disconnect();
    };
  }, []);
};

export const useScrollDepth = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const fired = new Set<number>();
    const pagePath = window.location.pathname || "/";
    let attached = false;

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return;
      const percent = Math.round((scrollTop / scrollHeight) * 100);

      SCROLL_THRESHOLDS.forEach((threshold) => {
        if (percent < threshold || fired.has(threshold)) return;
        const key = `${SESSION_SCROLL_PREFIX}:${pagePath}:${threshold}`;
        if (isSessionMarked(key)) {
          fired.add(threshold);
          return;
        }
        markSession(key);
        fired.add(threshold);
        trackScrollDepth(threshold);
      });
    };

    const cancelIdle = scheduleWhenIdle(() => {
      window.addEventListener("scroll", handleScroll, { passive: true });
      attached = true;
      handleScroll();
    }, 2000);

    return () => {
      cancelIdle();
      if (attached) {
        window.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);
};

export const useOutboundClickTracker = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    let attached = false;

    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target) return;
      const link = target.closest<HTMLAnchorElement>("a[href]");
      if (!link) return;
      const href = link.getAttribute("href");
      if (!href) return;

      let url: URL | null = null;
      if (!href.startsWith("mailto:") && !href.startsWith("tel:")) {
        try {
          url = new URL(href, window.location.href);
        } catch {
          url = null;
        }
      }

      const isOutbound = href.startsWith("mailto:")
        || href.startsWith("tel:")
        || (url ? url.hostname !== window.location.hostname : false);

      if (!isOutbound) return;

      const linkDomain = href.startsWith("mailto:")
        ? "mailto"
        : href.startsWith("tel:")
          ? "tel"
          : url?.hostname;

      if (!linkDomain) return;

      trackOutboundClick({
        link_domain: linkDomain,
        link_label: getLabel(link),
        section_id: getSectionId(link),
      });
    };

    const cancelIdle = scheduleWhenIdle(() => {
      document.addEventListener("click", onClick, true);
      attached = true;
    }, 2000);

    return () => {
      cancelIdle();
      if (attached) {
        document.removeEventListener("click", onClick, true);
      }
    };
  }, []);
};

export const useCtaClickTracker = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    let attached = false;

    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target) return;

      const ctaElement = target.closest<HTMLElement>("[data-cta-id]");
      if (!ctaElement) return;

      const ctaId = ctaElement.dataset.ctaId;
      if (!ctaId) return;

      trackCtaClick({
        cta_id: ctaId,
        cta_label: getLabel(ctaElement),
        section_id: getSectionId(ctaElement),
        href: getHref(ctaElement),
      });

      const planName =
        ctaElement.dataset.planName ||
        ctaElement.closest<HTMLElement>("[data-plan-name]")?.dataset.planName;

      if (planName) {
        trackPricingPlanSelect({
          plan_name: planName,
          currency: "CHF",
          section_id: "pricing",
        });
      }
    };

    const cancelIdle = scheduleWhenIdle(() => {
      document.addEventListener("click", onClick, true);
      attached = true;
    }, 2000);

    return () => {
      cancelIdle();
      if (attached) {
        document.removeEventListener("click", onClick, true);
      }
    };
  }, []);
};
